import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  pendingActions?: PendingAction[];
  isStreaming?: boolean;
}

export interface PendingAction {
  id: string;
  type: 'create' | 'update' | 'delete' | 'archive';
  entityType: 'task' | 'follow_up' | 'meeting' | 'invoice_reminder' | 'lead';
  description: string;
  confirmed: boolean | null;
  data: Record<string, any>;
  toolName: string;
}

interface CatalystFlowContextType {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  messages: Message[];
  sendMessage: (content: string) => Promise<void>;
  confirmAction: (actionId: string) => Promise<void>;
  rejectAction: (actionId: string) => void;
  isLoading: boolean;
  openWithPrompt: (prompt: string) => void;
  clearMessages: () => void;
  pendingPrompt: string | null;
  clearPendingPrompt: () => void;
}

const CatalystFlowContext = createContext<CatalystFlowContextType | undefined>(undefined);

export function CatalystFlowProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pendingPrompt, setPendingPrompt] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const openWithPrompt = useCallback((prompt: string) => {
    setPendingPrompt(prompt);
    setIsOpen(true);
  }, []);

  const clearPendingPrompt = useCallback(() => {
    setPendingPrompt(null);
  }, []);

  const clearMessages = useCallback(() => {
    setMessages([]);
  }, []);

  const sendMessage = useCallback(async (content: string) => {
    if (!content.trim() || isLoading) return;

    // Add user message
    const userMessage: Message = {
      id: crypto.randomUUID(),
      role: 'user',
      content: content.trim(),
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    // Cancel any existing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error('Please sign in to use Ask Catalyst Flow');
        setIsLoading(false);
        return;
      }

      // Get conversation history (last 10 messages for context)
      const conversationHistory = messages.slice(-10).map(m => ({
        role: m.role,
        content: m.content
      }));

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ask-catalyst-flow`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          message: content,
          conversationHistory,
        }),
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        if (response.status === 403 && errorData.upgrade_required) {
          toast.error('Ask Catalyst Flow is available exclusively for PRO users');
          setIsLoading(false);
          return;
        }
        if (response.status === 429) {
          toast.error('Rate limit reached. Please wait a moment and try again.');
          setIsLoading(false);
          return;
        }
        if (response.status === 402) {
          toast.error('AI credits exhausted. Please add more credits.');
          setIsLoading(false);
          return;
        }
        
        throw new Error(errorData.error || 'Failed to get response');
      }

      // Create streaming assistant message
      const assistantMessageId = crypto.randomUUID();
      let assistantContent = '';
      let toolCalls: any[] = [];

      setMessages(prev => [...prev, {
        id: assistantMessageId,
        role: 'assistant',
        content: '',
        timestamp: new Date(),
        isStreaming: true,
      }]);

      // Process SSE stream
      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response body');

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Process complete lines
        let newlineIndex: number;
        while ((newlineIndex = buffer.indexOf('\n')) !== -1) {
          let line = buffer.slice(0, newlineIndex);
          buffer = buffer.slice(newlineIndex + 1);

          if (line.endsWith('\r')) line = line.slice(0, -1);
          if (line.startsWith(':') || line.trim() === '') continue;
          if (!line.startsWith('data: ')) continue;

          const jsonStr = line.slice(6).trim();
          if (jsonStr === '[DONE]') continue;

          try {
            const parsed = JSON.parse(jsonStr);
            const delta = parsed.choices?.[0]?.delta;
            
            if (delta?.content) {
              assistantContent += delta.content;
              setMessages(prev => prev.map(m => 
                m.id === assistantMessageId 
                  ? { ...m, content: assistantContent }
                  : m
              ));
            }

            // Handle tool calls
            if (delta?.tool_calls) {
              delta.tool_calls.forEach((tc: any) => {
                if (tc.index !== undefined) {
                  if (!toolCalls[tc.index]) {
                    toolCalls[tc.index] = { id: tc.id, function: { name: '', arguments: '' } };
                  }
                  if (tc.function?.name) {
                    toolCalls[tc.index].function.name = tc.function.name;
                  }
                  if (tc.function?.arguments) {
                    toolCalls[tc.index].function.arguments += tc.function.arguments;
                  }
                }
              });
            }
          } catch {
            // Incomplete JSON, wait for more data
            buffer = line + '\n' + buffer;
            break;
          }
        }
      }

      // Process tool calls into pending actions
      const pendingActions: PendingAction[] = [];
      for (const tc of toolCalls) {
        if (tc?.function?.name && tc?.function?.arguments) {
          try {
            const args = JSON.parse(tc.function.arguments);
            const action: PendingAction = {
              id: tc.id || crypto.randomUUID(),
              type: tc.function.name.includes('archive') || tc.function.name.includes('delete') ? 'archive' : 
                    tc.function.name.includes('update') ? 'update' : 'create',
              entityType: getEntityType(tc.function.name),
              description: getActionDescription(tc.function.name, args),
              confirmed: null,
              data: args,
              toolName: tc.function.name,
            };
            pendingActions.push(action);
          } catch (e) {
            console.error('Failed to parse tool call:', e);
          }
        }
      }

      // Handle empty response (reasoning model used all tokens on thinking)
      if (!assistantContent.trim() && pendingActions.length === 0) {
        assistantContent = "I couldn't generate a complete response. This sometimes happens with complex queries. Please try asking a simpler question or breaking it into parts.";
      }

      // Update final message with pending actions
      setMessages(prev => prev.map(m => 
        m.id === assistantMessageId 
          ? { ...m, content: assistantContent, isStreaming: false, pendingActions: pendingActions.length > 0 ? pendingActions : undefined }
          : m
      ));

    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }
      console.error('Catalyst Flow error:', error);
      toast.error('Failed to get response. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [messages, isLoading]);

  const confirmAction = useCallback(async (actionId: string) => {
    // Find the action
    let action: PendingAction | undefined;
    let messageId: string | undefined;
    
    for (const msg of messages) {
      const found = msg.pendingActions?.find(a => a.id === actionId);
      if (found) {
        action = found;
        messageId = msg.id;
        break;
      }
    }

    if (!action || !messageId) return;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error('Please sign in');
        return;
      }

      // Execute the action based on type
      let success = false;
      
      switch (action.toolName) {
        case 'create_task':
          const { error: taskError } = await supabase.from('tasks').insert({
            user_id: session.user.id,
            title: action.data.title,
            due_date: action.data.due_date,
            priority: action.data.priority || 'medium',
            description: action.data.description,
            status: 'todo',
          });
          success = !taskError;
          if (taskError) console.error('Create task error:', taskError);
          break;

        case 'create_calendar_event':
          const { error: eventError } = await supabase.from('calendar_events').insert({
            user_id: session.user.id,
            title: action.data.title,
            event_type: action.data.event_type,
            start_time: action.data.start_time,
            linked_entity_id: action.data.linked_entity_id,
            linked_entity_type: action.data.linked_entity_type,
            status: 'scheduled',
          });
          success = !eventError;
          if (eventError) console.error('Create event error:', eventError);
          break;

        case 'update_deal_stage':
          const { error: stageError } = await supabase.from('sales_leads')
            .update({ stage: action.data.new_stage })
            .eq('id', action.data.lead_id);
          success = !stageError;
          if (stageError) console.error('Update stage error:', stageError);
          break;

        case 'update_task_status':
          const updates: any = { status: action.data.status };
          if (action.data.status === 'done') {
            updates.completed_at = new Date().toISOString();
          }
          const { error: statusError } = await supabase.from('tasks')
            .update(updates)
            .eq('id', action.data.task_id);
          success = !statusError;
          if (statusError) console.error('Update task status error:', statusError);
          break;

        case 'archive_items':
          const archivePromises = action.data.item_ids.map((id: string) => {
            const table = action.data.item_type === 'lead' ? 'sales_leads' : 
                          action.data.item_type === 'invoice' ? 'invoices' : 'tasks';
            return supabase.from(table)
              .update({ is_archived: true, archived_at: new Date().toISOString() })
              .eq('id', id);
          });
          const results = await Promise.all(archivePromises);
          success = results.every(r => !r.error);
          break;

        case 'send_invoice_reminder':
          // This would trigger the invoice reminder function
          toast.info('Invoice reminder scheduled');
          success = true;
          break;

        default:
          toast.error('Unknown action type');
          return;
      }

      if (success) {
        toast.success('Action completed successfully');
        // Update the action status
        setMessages(prev => prev.map(m => {
          if (m.id === messageId && m.pendingActions) {
            return {
              ...m,
              pendingActions: m.pendingActions.map(a => 
                a.id === actionId ? { ...a, confirmed: true } : a
              ),
            };
          }
          return m;
        }));
      } else {
        toast.error('Failed to execute action');
      }
    } catch (error) {
      console.error('Action execution error:', error);
      toast.error('Failed to execute action');
    }
  }, [messages]);

  const rejectAction = useCallback((actionId: string) => {
    setMessages(prev => prev.map(m => {
      if (m.pendingActions) {
        return {
          ...m,
          pendingActions: m.pendingActions.map(a => 
            a.id === actionId ? { ...a, confirmed: false } : a
          ),
        };
      }
      return m;
    }));
    toast.info('Action cancelled');
  }, []);

  return (
    <CatalystFlowContext.Provider value={{
      isOpen,
      setIsOpen,
      messages,
      sendMessage,
      confirmAction,
      rejectAction,
      isLoading,
      openWithPrompt,
      clearMessages,
      pendingPrompt,
      clearPendingPrompt,
    }}>
      {children}
    </CatalystFlowContext.Provider>
  );
}

export function useCatalystFlow() {
  const context = useContext(CatalystFlowContext);
  if (!context) {
    throw new Error('useCatalystFlow must be used within a CatalystFlowProvider');
  }
  return context;
}

// Helper functions
function getEntityType(toolName: string): PendingAction['entityType'] {
  if (toolName.includes('task')) return 'task';
  if (toolName.includes('deal') || toolName.includes('lead')) return 'lead';
  if (toolName.includes('calendar') || toolName.includes('meeting')) return 'meeting';
  if (toolName.includes('invoice')) return 'invoice_reminder';
  return 'task';
}

function getActionDescription(toolName: string, args: any): string {
  switch (toolName) {
    case 'create_task':
      return `Create task: "${args.title}"${args.due_date ? ` (due: ${args.due_date})` : ''}`;
    case 'create_calendar_event':
      return `Schedule ${args.event_type}: "${args.title}"`;
    case 'update_deal_stage':
      return `Move deal to "${args.new_stage}" stage`;
    case 'update_task_status':
      return `Mark task as "${args.status}"`;
    case 'archive_items':
      return `Archive ${args.item_ids?.length || 0} ${args.item_type}(s): ${args.reason}`;
    case 'send_invoice_reminder':
      return `Send payment reminder for invoice`;
    default:
      return `Execute ${toolName}`;
  }
}
